import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { MessageSquare, Search, Download, Clock } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function MessagesPage() {
  const [messages, setMessages] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback((query = "") => {
    api.get(`/admin/attendee-messages${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => setMessages(r.data))
      .catch(() => setMessages([]));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
  }, [q, load]);

  const exportCsv = () => {
    if (!messages?.length) return;
    const rows = [["Date", "Experience", "Attendee name", "Attendee email", "Video time", "Message"]];
    messages.forEach((m) => rows.push([
      m.created_at, m.webinar_title, m.attendee_name, m.attendee_email, m.video_timestamp, m.message,
    ]));
    const esc = (c) => `"${String(c ?? "").replace(/"/g, '""')}"`;
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "attendee-messages.csv";
    a.click();
  };

  return (
    <div className="p-8" data-testid="messages-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tighter flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-[#6D28B5]" /> Messages
          </h1>
          <p className="text-[#4B5563] mt-1">Every message attendees post in the watch room, with the moment in the video it was sent.</p>
        </div>
        <button
          onClick={exportCsv}
          disabled={!messages?.length}
          data-testid="messages-export-csv"
          className="border border-[#0A0A0A] hover:bg-[#F3F4F6] disabled:opacity-40 px-4 py-2.5 font-semibold flex items-center gap-2"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email, message or experience"
          data-testid="messages-search"
          className="w-full border border-[#0A0A0A] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]"
        />
      </div>

      {messages === null ? (
        <div className="text-[#9CA3AF]">Loading…</div>
      ) : messages.length === 0 ? (
        <div className="border border-dashed border-[#9CA3AF] bg-white p-16 text-center">
          <p className="text-[#4B5563] font-medium">{q ? "No messages match your search." : "No attendee messages yet."}</p>
        </div>
      ) : (
        <div className="bg-white border border-[#0A0A0A]">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#E5E7EB] text-xs uppercase tracking-wider font-bold text-[#4B5563]">
            <div className="col-span-3">Attendee</div>
            <div className="col-span-3">Experience</div>
            <div className="col-span-4">Message</div>
            <div className="col-span-1 text-center">Video</div>
            <div className="col-span-1">Sent</div>
          </div>
          {messages.map((m) => (
            <div key={m.id} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#E5E7EB] last:border-0 text-sm items-start" data-testid={`message-row-${m.id}`}>
              <div className="col-span-3 min-w-0">
                <div className="font-semibold truncate">{m.attendee_name || "—"}</div>
                <div className="text-xs text-[#6B7280] truncate">{m.attendee_email}</div>
              </div>
              <div className="col-span-3 truncate text-[#4B5563]">{m.webinar_title}</div>
              <div className="col-span-4 break-words leading-relaxed">{m.message}</div>
              <div className="col-span-1 text-center">
                <span className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-[#6D28B5] bg-[#F5F2FC] px-2 py-1 rounded">
                  <Clock className="w-3 h-3" /> {m.video_timestamp}
                </span>
              </div>
              <div className="col-span-1 text-xs text-[#6B7280]">{fmtDate(m.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
